import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";
import "./change-password.scss";
import { getCurrentUser } from "../../common/routines/user-operations";
import * as loginAPI from "../../api/login";
import { useSetLoading } from "../../common/hooks/appearance";
import { AlertComponent } from "../../common/components";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import * as resetPasswordAPI from "../../api/resetpassword";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AlertMessage {
  open: boolean;
  message?: any;
  type?: "success" | "error" | "info" | "warning";
  onClose?: () => void;
}

const ChangePassword: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [isFirstTimeLogin, setIsFirstTimeLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });

  const [showPassword, setPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toggleShowPasswordVisibility = () => {
    setPassword(!showPassword);
  };
  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const firstTimeLogin = localStorage.getItem("firstTimeLogin");
  const currentUser = getCurrentUser();
  const userName = currentUser?.username;

  useEffect(() => {
    if (firstTimeLogin === "true") {
      setIsFirstTimeLogin(true);
    }
  }, []);

  const validationSchema = Yup.object({
    currentPassword: Yup.string().required("Current password is required"),
    newPassword: Yup.string()
      .required("New password is required")
      .min(8, "Password must be at least 8 characters")
      .max(30, "Password length must be less than 15 characters")
      .matches(/[A-Z]/, "Must contain at least one uppercase letter")
      .matches(/\d/, "Must contain at least one number")
      .matches(/[^a-zA-Z0-9]/, "Must contain at least one special character")
      .notOneOf(
        [Yup.ref("currentPassword")],
        "New password must be different from the current password."
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Password must match")
      .required("Confirm password is required"),
  });

  const handleClose = (afterClose?: () => void) => {
    setFadeOut(true);
    setTimeout(() => {
      onClose();
      setFadeOut(false);
      if (afterClose) afterClose();
    }, 300);
  };

  const onSubmit = async (
    values: typeof initialValues,
    { setSubmitting, resetForm }: FormikHelpers<typeof initialValues>
  ) => {
    setIsLoading(true);
    try {
      const payload = {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmNewPassword: values.confirmPassword,
      };
      const response = await loginAPI.changePassword(payload);

      if (response.status === 200 && response.data.success) {
        setAlertMessage({
          open: true,
          message: "Password changed successfully!",
          type: "success",
        });

        setTimeout(() => {
          setAlertMessage({ open: false });
          resetForm();
          handleClose(() => setIsFirstTimeLogin(false));
        }, 1000);
      }
    } catch (error: any) {
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong!",
        type: "error",
      });
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    setIsLoading(true);
    try {
      const response: any = await resetPasswordAPI?.forgotpassword({
        username: userName,
      });

      if (response?.data?.data?.message) {
        setAlertMessage({
          open: true,
          message: response?.data?.data?.message,
          type: "success",
        });
        setTimeout(() => {
          setAlertMessage({ open: false });
          handleClose();
        }, 1000);
      }
      setIsLoading(false);
    } catch (error: any) {
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong!",
        type: "error",
      });
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => e.stopPropagation()}>
      <div className={` ${fadeOut ? "fadeOut" : ""}`}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          validateOnChange
          validateOnBlur
          validateOnMount
        >
          {({ setFieldTouched, dirty, isValid, isSubmitting }) => (
            <Form className="changepassword-wrapper">
              <div className="formHeader">
                <h2>Change Password</h2>
                <span className="subTitle">
                  Set a strong password to prevent unauthorized access to your
                  account
                </span>
              </div>

              <div className="formBody">
                <div className="field">
                  <label htmlFor="currentPassword">Current Password</label>
                  <Field
                    type={showCurrentPassword ? "text" : "password"}
                    id="currentPassword"
                    name="currentPassword"
                    placeholder="Enter Current Password "
                    onInput={(e: any) =>
                      setFieldTouched("currentPassword", true)
                    }
                    maxLength={30}
                  />
                  <span
                    className="visibility-icon"
                    onClick={toggleCurrentPasswordVisibility}
                  >
                    {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                  </span>
                  <ErrorMessage
                    name="currentPassword"
                    component="div"
                    className="errorMessage"
                  />
                </div>

                {!isFirstTimeLogin && (
                  <div className="helperLinks">
                    <button onClick={handleForgotPassword} type="button">
                      Forgot Password?
                    </button>
                  </div>
                )}

                <div className="field">
                  <label htmlFor="newPassword">New Password</label>
                  <Field
                    type={showPassword ? "text" : "password"}
                    id="newPassword"
                    name="newPassword"
                    placeholder="Enter New Password"
                    onInput={(e: any) => setFieldTouched("newPassword", true)}
                    maxLength={30}
                  />
                  <span
                    className="visibility-icon"
                    onClick={toggleShowPasswordVisibility}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </span>
                  <ErrorMessage
                    name="newPassword"
                    component="div"
                    className="errorMessage"
                  />
                </div>

                <div className="field">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <Field
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="Enter Confirm Password"
                    onInput={(e: any) =>
                      setFieldTouched("confirmPassword", true)
                    }
                    maxLength={30}
                  />
                  <span
                    className="visibility-icon"
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </span>
                  <ErrorMessage
                    name="confirmPassword"
                    component="div"
                    className="errorMessage"
                  />
                </div>
              </div>

              <div className="formFooter">
                {!isFirstTimeLogin && (
                  <button
                    className="cancelButton"
                    type="button"
                    onClick={() =>handleClose()}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="submitButton"
                  disabled={isSubmitting || isLoading || !isValid}
                >
                  Save
                </button>
              </div>

              {alertMessage.open && (
                <AlertComponent
                  message={alertMessage.message}
                  type={alertMessage.type || "info"}
                  open={alertMessage.open}
                  onClose={() => setAlertMessage({ open: false })}
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ChangePassword;
